import { DashboardBookingCard } from '@/features/consumer/dashboard-booking-card';
import { useAuth } from '@/context/auth-context';
import { useGetBookingQuery } from '@/hooks/queries/use-get-booking.query';
import { useGetBookingDetailQuery } from '@/hooks/queries/use-get-booking-detail.query';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';

/**
 * Matches customer app `YourLatestBooking`:
 * - `GET /booking/get/{userid}/?sort_by=created_date&sort_order=desc`
 * - First list row where `status` is `ongoing`
 * - `GET /booking/{id}/` for merged detail (start/end time, cleaners, etc.)
 */
export function LatestBookingWidgetDashboard() {
  const { user } = useAuth();
  const userId = user?.userid || '';

  const {
    data: bookingsData,
    isLoading,
    error,
  } = useGetBookingQuery(userId, {
    sort_by: 'created_date',
    sort_order: 'desc',
  });

  const listBooking = useMemo(() => {
    const all = bookingsData?.bookings ?? [];
    return all.find(b => (b.status || '').toLowerCase() === 'ongoing') ?? null;
  }, [bookingsData]);

  const bookingId = listBooking?.booking_id ?? '';

  const { data: bookingDetail, isLoading: detailLoading } =
    useGetBookingDetailQuery(bookingId);

  const booking = useMemo(() => {
    if (!listBooking) return null;
    if (!bookingDetail) return listBooking;
    return {
      ...listBooking,
      ...bookingDetail,
      total_amount: parseFloat(String(bookingDetail.total_amount)),
      cleaners: bookingDetail.cleaners?.length
        ? bookingDetail.cleaners
        : listBooking.cleaners,
    };
  }, [listBooking, bookingDetail]);

  const sectionTitle = (
    <h2 className="text-lg font-bold text-black dark:text-white">
      Ongoing booking
    </h2>
  );

  if (isLoading) {
    return (
      <section className="space-y-4">
        {sectionTitle}
        <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white py-12 dark:border-neutral-800 dark:bg-neutral-950">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-[#ED7034]" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="space-y-4">
        {sectionTitle}
        <div className="py-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Failed to load bookings. Please try again.
          </p>
        </div>
      </section>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <section className="space-y-4">
      {sectionTitle}
      <div className="relative">
        {detailLoading && !bookingDetail ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/70 dark:bg-neutral-950/70">
            <Loader2 className="h-8 w-8 animate-spin text-[#ED7034]" />
          </div>
        ) : null}
        <DashboardBookingCard
          booking={booking}
          variant="history"
          showReviewAndTip={false}
          viewDetailsSearch={{ ref: 'dashboard' }}
        />
      </div>
    </section>
  );
}
